import { useAuth } from './context/auth'
import { Routes, Route, Navigate } from 'react-router-dom'
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/LandingPage'
import ServiceDetailPage from './pages/ServiceDetailPage'
import PartsMarketplacePage from './pages/PartsMarketplacePage'
import CustomerDashboard from './dashboards/CustomerDashboard'
import TowerDashboard from './dashboards/TowerDashboard'
import GarageDashboard from './dashboards/GarageDashboard'
import PartsVendorDashboard from './dashboards/PartsVendorDashboard'
import AdminDashboard from './dashboards/AdminDashboard'
import InsurerDashboard from './dashboards/InsurerDashboard'
import AdminActivityPage from './pages/AdminActivityPage'
import { CustomerTracking } from './screens/customer/CustomerTracking'
import TowingRequestPage from './pages/TowingRequestPage'
import TowerPaymentApprovalPage from './pages/TowerPaymentApprovalPage'
import PaymentTrackingPage from './pages/PaymentTrackingPage'
import TowerOnboardingDashboard from './components/TowerOnboardingDashboard'
import PartsVendorOnboarding from './components/PartsVendorOnboarding'
import UnifiedDashboard from './components/UnifiedDashboard'
import UnifiedOnboarding from './components/UnifiedOnboarding'
import { hasRole, getAllRoles } from './lib/roleUtils'

// Check if user needs onboarding
function NeedsOnboarding({ profile, children }: { profile: any, children: React.ReactNode }) {
  const userRoles = getAllRoles(profile)
  const hasTowerRole = hasRole(profile, 'tower')
  const hasPartsVendorRole = hasRole(profile, 'parts_vendor')

  // Multi-role users go to unified onboarding
  if (userRoles.length > 1 && !profile.onboarding_completed) {
    return <Navigate to="/unified-onboarding" replace />
  }

  // Single role users go to specific onboarding
  const needsTowerOnboarding = hasTowerRole && !profile.onboarding_completed
  const needsVendorOnboarding = hasPartsVendorRole && !profile.onboarding_completed

  if (needsTowerOnboarding) {
    return <Navigate to="/tower-onboarding" replace />
  }
  if (needsVendorOnboarding) {
    return <Navigate to="/vendor-onboarding" replace />
  }
  return <>{children}</>
}

export default function App() {
  const { session, profile, loading, signOut } = useAuth()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="logo">Mkononi</div>
        <div className="loading-spinner" />
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Loading your profile...</p>
      </div>
    )
  }

  // If session exists but profile doesn't AND loading is complete, show error screen
  if (!loading && session && !profile) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: 'var(--bg)'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h1 style={{ marginBottom: '1rem' }}>Unable to Load Profile</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem', maxWidth: '400px' }}>
          We're having trouble loading your account information. This might be due to a temporary issue.
        </p>
        <button
          onClick={signOut}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Sign Out and Try Again
        </button>
      </div>
    )
  }

  if (!session || !profile) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/service" element={<ServiceDetailPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/tracking/:jobId" element={<CustomerTracking />} />
      <Route path="/towing-request" element={<TowingRequestPage />} />
      <Route path="/tower-payment/:jobId" element={<TowerPaymentApprovalPage />} />
      <Route path="/payments" element={<PaymentTrackingPage />} />
      <Route path="/parts-marketplace" element={<PartsMarketplacePage />} />
      <Route path="/admin/activity" element={<AdminActivityPage />} />
      <Route path="/tower-onboarding" element={<TowerOnboardingDashboard />} />
      <Route path="/vendor-onboarding" element={<PartsVendorOnboarding />} />
      <Route path="/unified-onboarding" element={<UnifiedOnboarding />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/" element={
        <NeedsOnboarding profile={profile}>
          {/* Multi-role users get unified dashboard */}
          {(profile.roles && profile.roles.length > 1) || (getAllRoles(profile).length > 1) ? (
            <UnifiedDashboard />
          ) : (
            <>
              {hasRole(profile, 'admin') && <AdminDashboard />}
              {hasRole(profile, 'customer') && <CustomerDashboard />}
              {hasRole(profile, 'tower') && <TowerDashboard />}
              {hasRole(profile, 'garage') && <GarageDashboard />}
              {hasRole(profile, 'parts_vendor') && <PartsVendorDashboard />}
              {hasRole(profile, 'insurer') && <InsurerDashboard />}
            </>
          )}
        </NeedsOnboarding>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
